'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';

export interface OrganizationHealth {
  clerkOrganizationId: string;
  name: string;
  slug: string;
  installationActive: boolean;
  onboardingComplete: boolean;
  subscriptionStatus: string;
}

/**
 * isHealthy is true when an organization can actually be used: it has an active VCS
 * installation and its subscription is not canceled. Used to auto-select a usable
 * organization and to recover a stranded user instead of landing them on a broken organization.
 */
export function isHealthy(organization: OrganizationHealth): boolean {
  return (
    organization.installationActive &&
    organization.subscriptionStatus !== 'canceled'
  );
}

/**
 * Hook for fetching the caller's organizations with per-organization health.
 * GET /api/v1/user/organizations
 *
 * Reachable even when the user's active organization is unhealthy (OrganizationOptional auth on the
 * backend), so the dashboard can pick a healthy organization and recover a stranded user.
 */
export function useOrganizationsWithHealth(options?: { enabled?: boolean }) {
  const { getToken } = useAuth();

  return useQuery<OrganizationHealth[]>({
    queryKey: ['organizations-with-health'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/user/organizations',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch organizations');
      }

      return data.organizations.map((organization) => ({
        clerkOrganizationId: organization.clerkOrganizationId,
        name: organization.name,
        slug: organization.slug,
        installationActive: organization.installationActive,
        onboardingComplete: organization.onboardingComplete,
        subscriptionStatus: organization.subscriptionStatus,
      }));
    },
    enabled: options?.enabled,
    staleTime: 30000,
  });
}
